/**
 * PDF 首页截图生成工具
 * 
 * 使用 pdfjs-dist 渲染 PDF 第一页为 JPEG 缩略图。
 * 支持 File 对象和远程 URL 两种输入方式。
 */
import * as pdfjsLib from 'pdfjs-dist';

// 设置 worker（使用 CDN 版本避免 Vite bundling 问题）
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.mjs`;

const THUMBNAIL_MAX_WIDTH = 600;
const THUMBNAIL_QUALITY = 0.85;

/**
 * 从 File 对象生成 PDF 首页缩略图
 */
export async function generatePdfThumbnailFromFile(file: File): Promise<Blob> {
  const arrayBuffer = await file.arrayBuffer();
  return renderFirstPage(new Uint8Array(arrayBuffer));
}

/**
 * 从 URL 生成 PDF 首页缩略图（用于已上传的文件）
 */
export async function generatePdfThumbnailFromUrl(url: string): Promise<Blob> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 30000); // 30s 超时

  try {
    const response = await fetch(url, { signal: controller.signal });
    clearTimeout(timeoutId);
    if (!response.ok) throw new Error(`Failed to fetch PDF: ${response.status}`);
    const arrayBuffer = await response.arrayBuffer();
    return renderFirstPage(new Uint8Array(arrayBuffer));
  } catch (err) {
    clearTimeout(timeoutId);
    throw err;
  }
}

/**
 * 核心：渲染 PDF 第一页为图片 Blob
 */
async function renderFirstPage(data: Uint8Array): Promise<Blob> {
  const pdf = await pdfjsLib.getDocument({ data }).promise;
  const page = await pdf.getPage(1);

  // 计算缩放比例，使宽度不超过 THUMBNAIL_MAX_WIDTH
  const viewport = page.getViewport({ scale: 1 });
  const scale = THUMBNAIL_MAX_WIDTH / viewport.width;
  const scaledViewport = page.getViewport({ scale });

  // 创建 canvas 渲染
  const canvas = document.createElement('canvas');
  canvas.width = scaledViewport.width;
  canvas.height = scaledViewport.height;

  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Failed to get canvas context');

  await page.render({
    canvasContext: ctx,
    viewport: scaledViewport,
  }).promise;

  // 转为 Blob
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        // 清理
        canvas.width = 0;
        canvas.height = 0;
        pdf.destroy();

        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Canvas toBlob failed'));
        }
      },
      'image/jpeg',
      THUMBNAIL_QUALITY
    );
  });
}
